import { get, unescape } from 'lodash'
import { Component } from 'react'
import styled, { css, withTheme } from 'styled-components'

import * as emojiConverter from '@/utilities/client-components/utilities/emojiConverter'
import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

import MarkdownParser from './MarkdownParser/MarkdownProcessor'
import {
  ColorsPropTypes,
  FontSizesPropTypes,
  FontWeightsPropTypes,
  HorizontalAlignmentsPropTypes,
  SpacingPropTypes
} from './PropTypes'
import TextFormatter from './TextFormatters'
import withSpacing from './WithSpacing'

const StyledTextBlock = styled.div`
  font-size: ${(props: any) =>
    themeUtilities.getFontSize(
      props.theme,
      // eslint-disable-next-line unicorn/explicit-length-check
      props.size || 'default'
    )}px;
  color: ${(props: any) => themeUtilities.getRgbaColor(props.theme, props.color || 'textBlock', props.isSubtle)};
  line-height: ${(props: any) =>
    get(props.theme, `fontLineHeight.${props.size}`, get(props.theme, `fontLineHeight.default`, 1.25))};
  font-weight: ${(props: any) =>
    get(props.theme, `fontWeights[${props.weight}]`, get(props.theme, `fontWeights.normal`))};
  text-align: ${(props: any) => props.horizontalAlignment || get(props.theme, 'horizontalAlignment', 'left')};
  ${(props: any) =>
    props.wrap &&
    css`
      word-wrap: break-word;
    `};
  ${(props: any) =>
    props.wrap &&
    props.maxLines &&
    css`
      max-height: ${props.maxLines * 1.3}em;
      overflow: hidden;
      background:
        linear-gradient(white 100%, rgb(255 255 255 / 0%)),
        url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgNjEgMTYuMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNjEgMTYuMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPjxnPjxwYXRoIGQ9Ik04LjEsMTYuMmM0LjUsMCw4LjEtMy43LDguMS04LjFTMTIuNSwwLDguMSwwQzMuNiwwLDAsMy43LDAsOC4xUzMuNywxNi4yLDguMSwxNi4yeiIvPjxwYXRoIGQ9Ik0zMC41LDE2LjJjNC41LDAsOC4xLTMuNyw4LjEtOC4xUzM0LjksMCwzMC41LDBjLTQuNSwwLTguMSwzLjctOC4xLDguMVMyNiwxNi4yLDMwLjUsMTYuMnoiLz48cGF0aCBkPSJNNTIuOSwwYy00LjUsMC04LjEsMy43LTguMSw4LjFzMy43LDguMSw4LjEsOC4xYzQuNSwwLDguMS0zLjcsOC4xLTguMVM1Ny4zLDAsNTIuOSwweiIvPjwvZz48L3N2Zz4=');
      background-repeat: no-repeat;
      background-position: bottom right 5px;
      background-size: 10px 10px;
      background-attachment: local, scroll;
    `};

  ${(props: any) =>
    !props.wrap &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `};

  a {
    color: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'link')};
  }
`

interface TextBlockProps {
  color?: unknown
  horizontalAlignment?: HorizontalAlignmentsPropTypes
  isSubtle?: boolean
  maxLines?: number
  separator?: boolean
  size?: FontSizesPropTypes
  spacing?: SpacingPropTypes
  speak?: string
  text: string | number
  weight?: FontWeightsPropTypes
  wrap?: boolean
}

class TextBlock extends Component<TextBlockProps> {
  static displayName = 'TextBlock'
  static propTypes = {
    color: ColorsPropTypes
  }

  static defaultProps = {
    text: '',
    wrap: true,
    size: 'default'
  }

  render() {
    const { text, size, maxLines, wrap, color, isSubtle, weight, horizontalAlignment } = this.props

    if (text && text.toString().trim()) {
      return (
        <StyledTextBlock
          data-id={'wg-textblock'}
          size={size}
          maxLines={maxLines}
          wrap={wrap ? 1 : 0}
          // @ts-expect-error
          color={color}
          isSubtle={isSubtle}
          weight={weight}
          horizontalAlignment={horizontalAlignment}
        >
          {MarkdownParser(emojiConverter.toEmoji(TextFormatter(unescape(text.toString()))))}
        </StyledTextBlock>
      )
    } else {
      return <div data-id="wg-textblock" />
    }
  }
}

const ACTextBlockWithSpacing = withSpacing(TextBlock)

export default withTheme(ACTextBlockWithSpacing)
